/* eslint-disable camelcase */
import { DaoModel } from '@dynameat/models';
import { store } from '@core/store';
import { PriceChange } from './priceChange';

export class ProductRecommendation extends DaoModel {
  constructor(guid, name_alt1, name_alt2, name_alt3, confirmed, master_sale_format,
    subordinate_products, recommendations_by_demand, min_range_price, max_range_price,
    is_subordinate_product) {
    super(guid);
    this.nameAlt1 = name_alt1 || '';
    this.nameAlt2 = name_alt2 || '';
    this.nameAlt3 = name_alt3 || '';
    this.initialy_confirmed = confirmed;
    this.master_sale_format = master_sale_format || '';
    this.subordinate_products = subordinate_products || [];
    this.recommendations_by_demand = recommendations_by_demand || {};
    this.is_subordinate_product = is_subordinate_product || false;
    this.min_range_price = min_range_price !== undefined ? min_range_price : null;
    this.max_range_price = max_range_price !== undefined ? max_range_price : null;
    this.confirmed = confirmed;
    ProductRecommendation.initializePriceStructure(this.recommendations_by_demand);
  }

  static toInstanceMap(list = []) {
    let subordinateProducts = list.reduce((total, next) => {
      total.push(...next.subordinate_products);
      return total;
    }, []);
    subordinateProducts = new Set(subordinateProducts);
    return list.reduce((map, el) => {
      const isSubordinateProduct = subordinateProducts.has(el.guid);
      let productRecommendation = null;
      productRecommendation = new this(el.guid, el.name_alt1, el.name_alt2, el.name_alt3, el.confirmed,
        el.master_sale_format, el.subordinate_products, el.recommendations_by_demand,
        el.min_range_price, el.max_range_price, isSubordinateProduct);
      map.set(productRecommendation.guid, productRecommendation);
      return map;
    }, new Map());
  }

  get commercial_name() {
    return this.nameAlt1;
  }

  getPriceChangesList(useOnlyConfirmedPrices = false) {
    const changesList = [];
    const context = this;
    Object.entries(this.recommendations_by_demand).forEach(([group, recommendationsByService]) => {
      Object.entries(recommendationsByService).forEach(([serviceGuid, recommendationBySaleRate]) => {
        Object.entries(recommendationBySaleRate).forEach(([saleRateGuid, pricesObj]) => {
          if (context.confirmed) {
            if (pricesObj.price !== pricesObj.recommendation_price) {
              changesList.push(new PriceChange(context.guid, group, serviceGuid, saleRateGuid, pricesObj.price));
            }
          } else if (!useOnlyConfirmedPrices && (pricesObj.previous_price !== pricesObj.recommendation_price)) {
            changesList.push(new PriceChange(context.guid, group, serviceGuid, saleRateGuid, pricesObj.previous_price));
          }
        });
      });
    });
    return changesList;
  }

  getPriceRangeLabel(groupCodeName, confirmed) {
    const priceList = this.getPriceList(groupCodeName);
    const prices = confirmed ? priceList.map((pricesObj) => (pricesObj.price)) : priceList.map((pricesObj) => (pricesObj.previous_price));
    return this.getPriceLabel(priceList, prices, confirmed);
  }

  getPriceLabel(priceList, prices, showIndicators = true) {
    let priceDiff = '';
    const { currency } = store.getters;
    const symbolPosition = currency ? currency.symbolPosition : '';
    const leftSymbol = symbolPosition === 'left' ? currency.symbol : '';
    const rightSymbol = symbolPosition === 'right' ? currency.symbol : '';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }
    if (min !== max) priceDiff = `${min} - ${max}`;
    else priceDiff = `${min}`;

    if (showIndicators) {
      const areAllPricesEqual = priceList.every((pricesObj) => (pricesObj.price === pricesObj.previous_price));
      if (areAllPricesEqual) priceDiff = `= ${leftSymbol}${priceDiff}${rightSymbol}`;
      else {
        const areAllPricesLower = priceList.every((pricesObj) => (pricesObj.price <= pricesObj.previous_price));
        priceDiff = areAllPricesLower ? `▼${leftSymbol}${priceDiff}${rightSymbol}` : `▲${leftSymbol}${priceDiff}${rightSymbol}`;
      }
    }
    return priceDiff;
  }

  getPriceList(groupCodeName) {
    const recommendationsByService = this.recommendations_by_demand[groupCodeName];
    const priceList = [];
    Object.values(recommendationsByService).forEach((recommendationsBySaleRate) => {
      Object.values(recommendationsBySaleRate).forEach((pricesObj) => {
        priceList.push(pricesObj);
      });
    });
    return priceList;
  }

  getPricesMap(groupCodeName) {
    const recommendationsByService = this.recommendations_by_demand[groupCodeName];
    const priceMap = new Map();
    Object.entries(recommendationsByService).forEach(([service, recommendationsBySaleRate]) => {
      Object.entries(recommendationsBySaleRate).forEach(([saleRate, pricesObj]) => {
        priceMap.set(`${service}-${saleRate}`, pricesObj);
      });
    });
    return priceMap;
  }

  static initializePriceStructure(recommendationsByDemand = {}) {
    Object.values(recommendationsByDemand).forEach((recommendationsByService) => {
      Object.values(recommendationsByService).forEach((recommendationBySaleRate) => {
        Object.values(recommendationBySaleRate).forEach((pricesObj) => {
          pricesObj.recommendation_price = pricesObj.price;
        });
      });
    });
  }

  hasConfirmationChanged() {
    return this.confirmed !== this.initialy_confirmed;
  }

  hasSomePriceChanged() {
    return this.getPriceChangesList(true).length > 0;
  }

  hasSomethingChanged() {
    return this.hasConfirmationChanged() || this.hasSomePriceChanged();
  }

  serializeRecommendationsByDemand() {
    const recommendationsByDemand = {};
    Object.entries(this.recommendations_by_demand).forEach(([group, recommendationsByService]) => {
      recommendationsByDemand[group] = {};
      this.serializeRecommendationsByService(recommendationsByService, recommendationsByDemand, group);
    });
    return recommendationsByDemand;
  }

  serializeRecommendationsByService(recommendationsByService, recommendationsByDemand, group) {
    Object.entries(recommendationsByService).forEach(([serviceGuid, recommendationsBySaleRate]) => {
      recommendationsByDemand[group][serviceGuid] = {};
      this.serializeRecommendationsBySaleRate(recommendationsBySaleRate, recommendationsByDemand, group, serviceGuid);
    });
  }

  serializeRecommendationsBySaleRate(recommendationsBySaleRate, recommendationsByDemand, group, serviceGuid) {
    Object.entries(recommendationsBySaleRate).forEach(([saleRateGuid, pricesObj]) => {
      recommendationsByDemand[group][serviceGuid][saleRateGuid] = {
        price: parseFloat(pricesObj.price),
        previous_price: parseFloat(pricesObj.previous_price)
      };
    });
  }

  serialize() {
    return {
      guid: this.guid,
      name_alt1: this.nameAlt1,
      name_alt2: this.nameAlt2,
      name_alt3: this.nameAlt3,
      confirmed: this.confirmed,
      master_sale_format: this.master_sale_format,
      subordinate_products: this.subordinate_products,
      min_range_price: this.min_range_price,
      max_range_price: this.max_range_price,
      recommendations_by_demand: this.serializeRecommendationsByDemand(),
    };
  }

  get allProductGuids() {
    return [this.guid, ...this.subordinate_products];
  }
}
